// The radio for BLE lamps — a bridge to the app-local Capacitor plugin
// BleAdvertisePlugin.java. NOTHING else in the app may advertise; every packet
// that reaches a lamp goes through broadcast() below.
//
// This is a DIFFERENT transport from core/ble/bleClient.js. That one connects
// to Smartify hardware over GATT. Lamps are never connected to: they sit and
// listen for advertising packets and act on any that carries an identity they
// were paired with. @capacitor-community/bluetooth-le cannot advertise at all,
// which is why this plugin exists.
//
// WHY SERVICE UUIDS AND NOT MANUFACTURER DATA
// The lamps listen for AD type 0x03 — "complete list of 16-bit service UUIDs"
// — carrying 26 bytes. Android has no API for writing a raw AD structure, but
// AdvertiseData.Builder.addServiceUuid() serialises 16-bit UUIDs into exactly
// that field, little endian, in insertion order. So 26 payload bytes go out as
// 13 service UUIDs. addManufacturerData() would emit type 0xFF and no lamp
// would ever answer.
//
// WHY THE ADVERTISEMENT MUST BE CONNECTABLE
// Android emits the 3-byte Flags header ONLY for connectable advertising. The
// reference packets begin 02 01 19 and the lamp firmware matches at fixed byte
// offsets, so without the header everything sits three bytes early and the
// lamp ignores it. Verified on hardware: identical payload, non-connectable →
// nothing; connectable → the lamp responds. IncludeFlags on the group row is
// this switch; it defaults on and exists only for hardware that wants the
// other layout.
//
// ⚠️ THE PLUGIN IS NATIVE AND SHIPS ONLY IN AN APK. OTA carries web assets, so
// this JavaScript can run on a phone whose APK predates the plugin. That must
// read "update the app", not "lamp did not respond" — see PLUGIN_MISSING.

import { Capacitor, registerPlugin } from '@capacitor/core';

const BleAdvertise = registerPlugin('BleAdvertise');

export const bleAdvertiseSupported = () => Capacitor.isNativePlatform()
  && Capacitor.getPlatform() === 'android';

// Reject codes the UI tells apart. The Java side uses the same strings.
export const NO_BT = 'NO_BT';
export const BT_OFF = 'BT_OFF';
export const NO_ADV = 'NO_ADV';
export const PERMISSION_DENIED = 'PERMISSION_DENIED';
export const ADV_FAIL = 'ADV_FAIL';
export const PLUGIN_MISSING = 'PLUGIN_MISSING';
export const UNSUPPORTED = 'UNSUPPORTED';

function isMissingPlugin(err) {
  if (err?.code === 'UNIMPLEMENTED') return true;
  const m = String(err?.message || '').toLowerCase();
  return m.includes('not implemented') || m.includes('does not have')
    || m.includes('not found') || m.includes('unimplemented');
}

function coded(code, message) {
  const e = new Error(message);
  e.code = code;
  return e;
}

/**
 * Split a 26-byte payload into 13 little-endian 16-bit service UUIDs.
 * Exported for the tests: a byte-order slip here produces packets that no lamp
 * answers and no error anywhere.
 */
export function toServiceUuids(payload) {
  if (payload.length % 2 !== 0) {
    throw new Error(`Payload must be an even number of bytes to map onto 16-bit UUIDs, got ${payload.length}`);
  }
  const uuids = [];
  for (let i = 0; i < payload.length; i += 2) {
    // eslint-disable-next-line no-bitwise
    uuids.push(payload[i] | (payload[i + 1] << 8));
  }
  return uuids;
}

/** The full 31-byte frame as the radio lays it out — for tests and the log. */
export function buildAdvertisingData(adFlag, adType, payload) {
  return Uint8Array.from([0x02, 0x01, adFlag, payload.length + 1, adType, ...payload]);
}

/**
 * Which layer is broken. Every failure of lamp control is one of: not the
 * Android app, the plugin missing from this APK, Bluetooth off, a radio that
 * cannot advertise, or the permission refused.
 *
 * -> { supported, plugin, radioReady, permission: 'granted'|'denied'|'prompt'|'unknown', detail }
 */
export async function diagnoseAdvertiser() {
  const out = { supported: bleAdvertiseSupported(), plugin: false, radioReady: null, permission: 'unknown', detail: '' };
  if (!out.supported) {
    out.detail = 'BLE lamps can only be controlled from the Android app.';
    return out;
  }
  try {
    const r = await BleAdvertise.isSupported();
    out.plugin = true;
    out.radioReady = !!r?.supported;
    out.permission = r?.permission || 'unknown';
    if (!out.radioReady) out.detail = r?.reason || 'This phone cannot broadcast BLE advertisements.';
  } catch (err) {
    if (isMissingPlugin(err)) {
      out.detail = 'This version of the app was installed before lamp support. Update the app from the Play Store.';
    } else {
      out.detail = String(err?.message || err);
    }
  }
  return out;
}

/**
 * Put one advertisement on the air for `durationMs`, then stop.
 *
 * The lamp needs to catch the packet while it happens to be listening, so the
 * broadcast is held open rather than sent once. ~400 ms is comfortable for a
 * command; pairing wants ~3 s. Resolves when the hold is over; rejects with a
 * `.code` from the list above.
 */
export async function broadcast(adv, durationMs = 400, connectable = true) {
  if (!bleAdvertiseSupported()) {
    throw coded(UNSUPPORTED, 'BLE lamps can only be controlled from the Android app.');
  }
  try {
    await BleAdvertise.start({ serviceUuids: toServiceUuids(adv.payload), connectable, durationMs });
  } catch (err) {
    if (isMissingPlugin(err)) {
      throw coded(PLUGIN_MISSING, 'This version of the app was installed before lamp support. Update the app from the Play Store.');
    }
    const e = coded(err?.code || ADV_FAIL, err?.message || 'Advertising failed');
    throw e;
  }
}
